import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  getColor,
  InputField,
  Tabs,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import { useNavigate } from "react-router-dom";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import utilsCommon from "../../shared/utils/common";
import { useTranslation } from "react-i18next";
import useWorkspaceStore from "../../redux/dispatcher/useWorkspaceStore";
import { nanoid } from "@reduxjs/toolkit";
const CheckSignup = () => {
  const params = new URLSearchParams(window.location.search);

  /** hook 정리 **/
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toasts = useToast();

  const { checkUserValidSignupUser } = useLoginStore();
  const { verifyMemberMailAgain } = useWorkspaceStore();

  /** State 정리 **/
  const [checkMail, changeMail] = useState(undefined);
  const [err, changeErr] = useState("NONE");
  const [type, changeType] = useState<
    "CHECK_TYPE" | "MAIL_SENT" | "INVALID_USER"
  >(
    params.get("status") && params.get("status") === "MAIL_SENT"
      ? "MAIL_SENT"
      : "CHECK_TYPE"
  );

  /** useEffect 정리 */
  useEffect(() => {
    if (type !== "CHECK_TYPE" && !checkMail && !params.get("userId")) {
      changeType("CHECK_TYPE");
    }
  }, [type]);

  /** 함수 정리 **/
  const handleUserValid = async () => {
    const email = checkMail ?? params.get("userId");
    if (!email) return;
    if (!utilsCommon.emailValidation(email)) {
      changeErr("INVALID_EMAIL");
      return;
    }
    const resp: any = await checkUserValidSignupUser(email);
    if (resp.error) {
      switch (resp.error.name) {
        case "NOT_FOUND_USER":
          changeType("INVALID_USER");
          return;
        case "USER_ALREADY_REGISTERED":
          changeErr("USER_ALREADY_REGISTERED");
          return;
      }
      return;
    } else {
      changeType("MAIL_SENT");
    }
  };
  /** UI: input error 메세지 */
  const renderErrorStr = (type: string) => {
    switch (type) {
      case "INVALID_EMAIL":
        return t("signup.noValidEmailType");
      case "USER_ALREADY_REGISTERED":
        return t("signup.alreadySignup");
    }
    return undefined;
  };
  /** UI: 이메일 확인 Form */
  const renderCheckForm = () => {
    return (
      <Box
        justifyContent={"center"}
        alignItems={"center"}
        style={{ marginTop: 30 }}
        maxWidth={420}
        width={"100%"}
        direction={"column"}
      >
        <Tabs
          type={"full"}
          selected={0}
          textAlign={"left"}
          tabs={[
            {
              color: "theme",
              text: `1. ${t("signup.checkValidUser")}`,
              isNew: false,
            },
            {
              color: "theme",
              text: `2. ${t("signup.enterInfo")}`,
              isNew: false,
            },
          ]}
          size={"md"}
        />
        <Box
          classes={"mg mt-40"}
          direction={"column"}
          alignItems={"left"}
          width={"360px"}
        >
          <Typography type="subtitle2">{t("signup.email")}</Typography>
          <InputField
            placeholder={t("signup.emailPlaceholder")}
            classes={"mg mt-8"}
            size={"md"}
            maxLength={60}
            value={checkMail}
            onChange={(e) => changeMail(e.target.value)}
            isError={err !== "NONE"}
            helpMessage={renderErrorStr(err)}
            onKeyUp={(e) => e.code === "Enter" && handleUserValid()}
          />
          <Button
            label={t("signup.checkValidUser")}
            classes={"mg mt-40"}
            btnStyle="fill"
            style={{ height: "48px" }}
            state={checkMail ? "default" : "disable"}
            onClick={handleUserValid}
          />
          <Box justifyContent={"center"} style={{ marginTop: 30 }}>
            <Typography
              type={"body3_rg"}
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {t("signup.alreadyAccount")}
            </Typography>
            <Typography
              type={"body3_rg"}
              classes={"mg ml-4 login-button"}
              exactColor={getColor("COLOR_GREEN_60")}
              onClick={() => navigate(`${process.env.PUBLIC_URL}/login`)}
            >
              {t("signup.goLogin")}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const handleReInviteUser = async (userId: string): Promise<void> => {
    const resp: any =
      window.eco.serviceType === "OCI"
        ? await verifyMemberMailAgain(userId)
        : await checkUserValidSignupUser(userId);
    if (resp.payload) {
      toasts.toastMsg(nanoid(), t("wsLogin.sendCertifyMsg"), "success");
    } else {
      toasts.toastMsg(nanoid(), t("wsLogin.errorSendCertifyMsg"), "error");
    }
  };
  /** UI: 메일 전송 완료 페이지 */
  const renderMailSent = () => {
    return (
      <Box
        style={{ marginTop: 70 }}
        maxWidth={767}
        direction="column"
        alignItems="center"
      >
        <img
          src={require("../../shared/images/pages/Signup/mail_sent.png")}
          alt={"logo"}
          height={160}
          width={300}
        />
        <Typography type="h1" classes="mg mt-20">
          {t("signup.registerMailTitle")}
        </Typography>
        <Typography
          type="body1_md"
          classes="mg mt-16 checkSignup-sendMail"
          textAlign="center"
          whiteSpace="break-spaces"
          exactColor={getColor("COLOR_TEXT_SECONDARY")}
        >
          {t("signup.registerMailInfo")}
        </Typography>
        <Typography type="body1_md" classes="mg mt-16">
          {t("signup.emailAddr")}: {checkMail ?? params.get("userId")}
        </Typography>
        <Box width={205} direction="column" alignItems="center">
          <Typography
            classes="checkSignup-sendMail--request"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            type="body3_rg"
            whiteSpace="break-spaces"
          >
            {t("signup.askResend")}
          </Typography>
          <Button
            label={t("signup.resend")}
            classes={"mg mt-10"}
            width={"calc(100% - 48px)"}
            btnStyle="fill"
            onClick={() =>
              handleReInviteUser(checkMail ?? params.get("userId") ?? "")
            }
          />
        </Box>
      </Box>
    );
  };
  /** UI: 가입 불가능 메일 페이지 */
  const renderInvalidUser = () => {
    return (
      <Box
        style={{ marginTop: 70 }}
        maxWidth={606}
        direction="column"
        alignItems="center"
      >
        <img
          src={require("../../shared/images/common/users.png")}
          alt={"logo"}
          height={160}
          width={300}
        />
        <Typography type="h1" classes="mg mt-20">
          {t("signup.noValidUserTitle")}
        </Typography>
        <Typography
          type="body1_md"
          classes="mg mt-16"
          textAlign="center"
          whiteSpace="break-spaces"
          exactColor={getColor("COLOR_TEXT_SECONDARY")}
        >
          {t("signup.noValidUser", { 0: checkMail })}
        </Typography>
        <Button
          label={t("signup.backToLogin")}
          style={{ marginTop: 34 }}
          btnStyle="fill"
          onClick={() => navigate(`${process.env.PUBLIC_URL}/login`)}
        />
      </Box>
    );
  };
  return (
    <Box width={"100%"} height={"100%"} classes={"checkSignup"}>
      <Box height={60} alignItems={"center"} justifyContent={"center"}>
        <img
          src={utilsCommon.renderWrapsodyECOLogo()}
          alt={"logo"}
          height={32}
          width={129}
          className="cursor pointer"
          onClick={() => navigate(`${process.env.PUBLIC_URL}/login`)}
        />
      </Box>
      {type === "CHECK_TYPE"
        ? renderCheckForm()
        : type === "MAIL_SENT"
        ? renderMailSent()
        : renderInvalidUser()}
    </Box>
  );
};

export default CheckSignup;
